import { cn } from '@client/lib';
import { formatCurrency } from '@shared';
import { CheckCircle } from 'lucide-react';

interface PlanCompleteSummaryProps {
  projectedLtcCost: number;
  selfFundingCapacity: number;
  remainingGap: number;
  selectedPolicy: {
    name: string;
    coverage: number;
  };
  updatedGap: number;
  updatedSurplus: number;
  accountsProtected: number;
  className?: string;
}

export function PlanCompleteSummary({
  projectedLtcCost,
  selfFundingCapacity,
  remainingGap,
  selectedPolicy,
  updatedGap,
  updatedSurplus,
  accountsProtected,
  className,
}: PlanCompleteSummaryProps) {
  return (
    <div className={cn('text-center', className)}>
      {/* Success Icon */}
      <div className="bg-primary mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl text-white">
        <CheckCircle className="h-10 w-10" />
      </div>

      {/* Title */}
      <h1 className="text-3xl font-bold text-gray-900">Your Plan is almost complete!</h1>
      <p className="mx-auto mt-3 max-w-lg text-gray-600">
        Complete your application for the selected policy to get your protection in place.
      </p>

      {/* Summary Card */}
      <div className="mx-auto mt-8 w-full rounded-xl border border-gray-200 bg-white p-6 text-left">
        <h3 className="mb-4 text-center font-semibold text-gray-900">Plan Summary</h3>

        {/* LTC Cost */}
        <div className="rounded-lg bg-gray-50 p-4 text-center">
          <p className="text-sm text-gray-500">Projected LTC Cost</p>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(projectedLtcCost)}</p>
        </div>

        {/* Comparison */}
        <div className="mt-4 grid grid-cols-2 gap-4">
          {/* Without Insurance */}
          <div className="rounded-lg bg-gray-50 p-4">
            <p className="mb-2 text-xs font-medium uppercase text-gray-500">Without Insurance</p>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Self-Funding</span>
                <span className="font-medium text-gray-900">
                  {formatCurrency(selfFundingCapacity)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Remaining Gap</span>
                <span
                  className={cn('font-medium', remainingGap > 0 ? 'text-red-600' : 'text-primary')}
                >
                  {formatCurrency(remainingGap)}
                </span>
              </div>
            </div>
          </div>

          {/* With Insurance */}
          <div className="bg-primary/5 rounded-lg p-4">
            <p className="text-primary mb-2 text-xs font-medium uppercase">With Insurance</p>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Policy Coverage</span>
                <span className="font-medium text-green-500">
                  +{formatCurrency(selectedPolicy.coverage)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Remaining Gap</span>
                {updatedSurplus > 0 ? (
                  <span className="flex items-center gap-1 font-medium text-green-600">
                    <span>✓</span>
                    <span>Fully Covered</span>
                  </span>
                ) : updatedGap > 0 ? (
                  <span className="font-medium text-red-600">-{formatCurrency(updatedGap)}</span>
                ) : (
                  <span className="flex items-center gap-1 font-medium text-green-600">
                    <span>✓</span>
                    <span>Fully Covered</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Accounts Protected */}
        <div className="mt-4 rounded-lg bg-gray-50 p-4 text-center">
          <p className="text-sm text-gray-500">This plan protects</p>
          <p className="text-primary text-2xl font-bold">{accountsProtected} accounts</p>
          <p className="text-sm text-gray-500">from being depleted by LTC costs</p>
        </div>

        {/* Selected Policy */}
        <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
          <span className="text-sm text-gray-600">Selected Policy</span>
          <span className="font-semibold text-gray-900">{selectedPolicy.name}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Coverage Amount</span>
          <span className="font-semibold text-green-500">
            {formatCurrency(selectedPolicy.coverage)}
          </span>
        </div>
      </div>
    </div>
  );
}

